export interface SearchableEvent { eventId: string; occurredAt: string; locationName: string; jobType?: string | null; notes?: string | null; odometer?: number | null; }
export type SearchField = 'location' | 'jobType' | 'notes' | 'odometer' | 'date';
export interface SearchMatch<T extends SearchableEvent = SearchableEvent> { event: T; field: SearchField; }
export interface DayGroup<T extends SearchableEvent = SearchableEvent> { day: string; events: T[]; }

const normalizeSearch = (text: string) => text.toLowerCase().replace(/[๐-๙]/g, digit => String('๐๑๒๓๔๕๖๗๘๙'.indexOf(digit)));
const calendarYear = (year: number) => year >= 2400 ? year - 543 : year;
const thaiMonths = ['มกราคม','กุมภาพันธ์','มีนาคม','เมษายน','พฤษภาคม','มิถุนายน','กรกฎาคม','สิงหาคม','กันยายน','ตุลาคม','พฤศจิกายน','ธันวาคม'];
const shortMonths = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];

// Convert Thai month names before tokenizing, so dates can be combined with locations.
function searchTerms(query: string): string[] {
  let text = normalizeSearch(query).trim();
  thaiMonths.forEach((month, index) => {
    for (const name of [month, shortMonths[index]]) {
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      text = text.replace(new RegExp(`(?:(\\d{1,2})\\s+)?${escaped}\\s*(\\d{4})`, 'g'), (_, day: string | undefined, year: string) => `${day ? day + '/' : ''}${index + 1}/${year}`);
    }
  });
  return text.split(/\s+/).filter(Boolean);
}

function matchTerm(event: SearchableEvent, term: string): SearchField | null {
  const iso = term.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const local = term.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  const month = term.match(/^(\d{1,2})\/(\d{4})$/);
  const dayMonth = term.match(/^(\d{1,2})\/(\d{1,2})$/);
  if (iso || local || month || dayMonth) {
    const key = bangkokDayKey(event.occurredAt);
    const [year, mon, day] = key.split('-').map(Number);
    const hit = iso ? year === calendarYear(Number(iso[1])) && mon === Number(iso[2]) && day === Number(iso[3])
      : local ? year === calendarYear(Number(local[3])) && mon === Number(local[2]) && day === Number(local[1])
      : month ? year === calendarYear(Number(month[2])) && mon === Number(month[1])
      : day === Number(dayMonth![1]) && mon === Number(dayMonth![2]);
    return hit ? 'date' : null;
  }
  const mileage = term.replace(/,/g, '');
  const range = mileage.match(/^(\d+(?:\.\d+)?)(?:\.\.|-)(\d+(?:\.\d+)?)$/);
  if (range) return event.odometer != null && event.odometer >= Number(range[1]) && event.odometer <= Number(range[2]) ? 'odometer' : null;
  if (/^\d+(?:\.\d+)?$/.test(mileage) && event.odometer != null && event.odometer === Number(mileage)) return 'odometer';
  if (normalizeSearch(event.locationName).includes(term)) return 'location';
  if (event.jobType && normalizeSearch(event.jobType).includes(term)) return 'jobType';
  if (event.notes && normalizeSearch(event.notes).includes(term)) return 'notes';
  return null;
}

export function searchHistoryEvents<T extends SearchableEvent>(rows: readonly T[], query: string): SearchMatch<T>[] {
  const terms = searchTerms(query);
  const needle = query.trim();
  if (!needle) return rows.map(event => ({ event, field: 'location' as const }));
  const matches: SearchMatch<T>[] = [];
  for (const event of rows) {
    const fields = terms.map(term => matchTerm(event, term));
    if (fields.every(field => field !== null)) matches.push({ event, field: fields.find(field => field === 'odometer' || field === 'date') ?? fields[0]! });
  }
  return matches;
}

// Bangkok calendar day (YYYY-MM-DD) — the same boundary the database summaries use.
export const bangkokDayKey = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });

export function groupEventsByDay<T extends SearchableEvent>(rows: readonly T[]): DayGroup<T>[] {
  const days = new Map<string, T[]>();
  for (const event of [...rows].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))) {
    const day = bangkokDayKey(event.occurredAt);
    days.set(day, [...(days.get(day) ?? []), event]);
  }
  return [...days].map(([day, events]) => ({ day, events })).sort((a, b) => b.day.localeCompare(a.day));
}

export function topLocations<T extends SearchableEvent>(rows: readonly T[], limit = 8): { name: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const row of rows) { const name = row.locationName.trim(); if (!name) continue; counts.set(name, (counts.get(name) ?? 0) + 1); }
  return [...counts].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'th')).slice(0, Math.max(0, limit));
}
